import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI } from '../../services/api';
import { 
  ShieldAlert, 
  Lock, 
  Mail, 
  User,
  Eye, 
  EyeOff, 
  ShieldCheck, 
  KeyRound, 
  ArrowRight, 
  HelpCircle,
  X,
  Smartphone,
  Check,
  ArrowLeft,
  Sparkles,
  Key
} from 'lucide-react';
import toast from 'react-hot-toast';
import BrandLogo from '../../components/BrandLogo';
import loginBg from '../../assets/loginbg.png';

export default function SuperAdminLogin() {
  const [email, setEmail] = useState('connect@sanglob.com');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [forgotModalOpen, setForgotModalOpen] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [isSubmittingRecovery, setIsSubmittingRecovery] = useState(false);

  // Microsoft SSO State
  const [msModalOpen, setMsModalOpen] = useState(false);
  const [msLoading, setMsLoading] = useState(false);
  const [msEmail, setMsEmail] = useState('connect@sanglob.com');

  // 2FA State
  const [twoFactorData, setTwoFactorData] = useState(null); // { tempToken, email, name, isSuperAdmin }
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [isVerifying2FA, setIsVerifying2FA] = useState(false);

  const { login, loginWithSSO, verify2FA, switchPortal, user, isAuthenticated, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const destination = location.state?.from?.pathname || '/superadmin/control-tower';

  // If already logged in as SuperAdmin, jump straight to Control Tower or requested route
  useEffect(() => {
    if (isAuthenticated && (isSuperAdmin || user?.type === 'SuperAdmin')) {
      navigate(destination, { replace: true });
    }
  }, [isAuthenticated, isSuperAdmin, user, navigate, destination]);

  // Handle Microsoft OAuth code if redirected back with ?code=
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    if (code) {
      handleProcessMicrosoftCode(code);
    }
  }, [location.search]);

  const handleProcessMicrosoftCode = async (code) => {
    setMsLoading(true);
    try {
      toast.loading('Validating Microsoft authentication...', { id: 'ms-auth' });
      const res = await authAPI.microsoftCallback({ code, roleTarget: 'SuperAdmin' });
      if (res?.data?.token) {
        await loginWithSSO(res.data, rememberMe);
        toast.success('Authenticated via Microsoft Entra ID. Entering Control Tower.', { id: 'ms-auth' });
        navigate(destination, { replace: true });
      }
    } catch (err) {
      console.error('Microsoft OAuth error:', err);
      toast.error(err.response?.data?.message || 'Microsoft authentication failed', { id: 'ms-auth' });
    } finally {
      setMsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter both SuperAdmin email and master security password');
      return;
    }

    setIsLoading(true);
    try {
      const res = await login(email, password, rememberMe, true, twoFactorCode.trim());
      if (res?.data?.requires2FA) {
        setTwoFactorData(res.data);
        toast.success('SuperAdmin 2FA Security Challenge');
        return;
      }

      toast.success('Sanglob Master SuperAdmin authenticated successfully');
      navigate(destination, { replace: true });
    } catch (error) {
      console.error('SuperAdmin auth error:', error);
      const msg = error.response?.data?.message || error.message || 'SuperAdmin authentication failed. Verify master credentials.';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!twoFactorCode || twoFactorCode.trim().length === 0) {
      toast.error('Please enter your 6-digit authentication code or backup recovery code');
      return;
    }

    setIsVerifying2FA(true);
    try {
      await verify2FA(twoFactorData.tempToken, twoFactorCode.trim(), rememberMe);
      toast.success('Two-Factor Authentication verified. Entering Control Tower.');
      navigate(destination, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid verification code. Please try again.');
    } finally {
      setIsVerifying2FA(false);
    }
  };

  const handleRecoverySubmit = async (e) => {
    e.preventDefault();
    if (!recoveryEmail) {
      toast.error('Please enter your authorized administrative email');
      return;
    }
    setIsSubmittingRecovery(true);
    try {
      const res = await authAPI.forgotPassword(recoveryEmail.trim().toLowerCase());
      setForgotModalOpen(false);
      toast.success(res.data?.message || `Password reset link dispatched to ${recoveryEmail}`);
      setRecoveryEmail('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to dispatch password recovery email');
    } finally {
      setIsSubmittingRecovery(false);
    }
  };

  const handleSSO = async (provider) => {
    if (provider === 'Microsoft') {
      try {
        setMsLoading(true);
        const res = await authAPI.getMicrosoftAuthUrl();
        if (res.data?.isConfigured && res.data?.authUrl) {
          window.location.href = res.data.authUrl;
        } else {
          setMsEmail(res.data?.defaultSuperAdminEmail || 'connect@sanglob.com');
          setMsModalOpen(true);
        }
      } catch (err) {
        setMsEmail('connect@sanglob.com');
        setMsModalOpen(true);
      } finally {
        setMsLoading(false);
      }
      return;
    }

    toast.info(`${provider} Single Sign-On initiated. Connecting to identity provider...`);
  };

  const handleConfirmMicrosoftSSO = async (e) => {
    if (e) e.preventDefault();
    if (!msEmail) {
      toast.error('Please enter the Microsoft 365 SuperAdmin email');
      return;
    }

    setMsLoading(true);
    try {
      const res = await authAPI.microsoftCallback({
        email: msEmail.trim().toLowerCase(),
        roleTarget: 'SuperAdmin'
      });

      if (res.data?.token) {
        await loginWithSSO(res.data, rememberMe);
        setMsModalOpen(false);
        toast.success(`Microsoft Entra ID (${msEmail}) verified. Entering Control Tower.`);
        navigate(destination, { replace: true });
      }
    } catch (err) {
      console.error('Microsoft SSO error:', err);
      toast.error(err.response?.data?.message || 'Microsoft authentication failed for this account');
    } finally {
      setMsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col justify-between font-sans bg-slate-950 text-slate-900 selection:bg-blue-600 selection:text-white overflow-x-hidden relative">
      
      {/* 1. TOP ROYAL BLUE HEADER BAR */}
      <header className="w-full bg-[#1e40af] bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-900 border-b border-blue-700/60 px-6 sm:px-10 py-2.5 flex items-center justify-between z-20 shadow-md shrink-0">
        <div className="flex items-center gap-3">
          <BrandLogo className="h-7 sm:h-8" dark={true} />
          <div className="hidden sm:flex items-center gap-2.5 pl-3 border-l border-blue-400/30">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-blue-950/40 text-blue-100 text-xs font-semibold border border-blue-400/30 shadow-2xs backdrop-blur-xs">
              <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
              <span>Platform SuperAdmin Enclave</span>
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <span className="hidden md:inline text-[11.5px] sm:text-xs md:text-[13px] font-medium tracking-wide text-blue-100/90 text-right select-none">
            Root Infrastructure & Multi-Tenant Governance
          </span>
          <Link 
            to="/login"
            className="inline-flex items-center gap-1 text-xs font-semibold text-blue-200 hover:text-white transition-colors bg-blue-950/40 hover:bg-blue-950/70 border border-blue-400/30 px-2.5 py-1 rounded-lg"
          >
            <span>Client Login</span>
            <span>→</span>
          </Link>
        </div>
      </header>

      {/* 2. MIDDLE SECTION: BACKGROUND IMAGE WITH RIGHT-SIDE FADE & FLOATING LOGIN CARD */}
      <main className="flex-1 w-full relative min-h-[calc(100vh-105px)] overflow-hidden flex items-center justify-end">
        
        {/* Background Artwork Layer */}
        <div className="absolute inset-0 z-0 select-none pointer-events-none">
          <img 
            src={loginBg} 
            alt="MIREVA Platform Control Enclave" 
            className="w-full h-full object-cover object-left lg:object-center"
          />
          {/* Smooth Right-Side Fade Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-slate-950/15 to-slate-950/45" />
          <div className="absolute right-0 top-0 bottom-0 w-full sm:w-2/3 lg:w-1/2 xl:w-2/5 bg-gradient-to-l from-slate-950/60 via-slate-950/25 to-transparent backdrop-blur-[1px]" />
        </div>

        {/* Floating Right-Aligned Login Card */}
        <div className="relative z-10 w-full max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-14 xl:px-20 py-6 sm:py-8 pb-16 flex items-center justify-center lg:justify-end">
          <div className="w-full max-w-[430px] sm:max-w-[450px] lg:max-w-[440px] xl:max-w-[460px] bg-white/95 backdrop-blur-2xl rounded-[28px] sm:rounded-[32px] border border-white/80 shadow-2xl shadow-blue-950/50 p-6 sm:p-8 text-slate-900 transition-all">
          
            {/* A. MIREVA BRAND HEADER LOCKUP */}
            <div className="flex flex-col items-center justify-center text-center">
              
              {/* Official MIREVA Brand Logo */}
              <BrandLogo className="h-9 sm:h-10" dark={false} />

              {/* Tagline Badges */}
              <div className="mt-1 space-y-0.5">
                <p className="text-[9px] sm:text-[10px] font-extrabold tracking-[0.22em] text-slate-700 uppercase">
                  AI-POWERED BUSINESS PLATFORM
                </p>
                <p className="text-[7.5px] sm:text-[8.5px] font-bold tracking-[0.18em] text-slate-400 uppercase">
                  FOR A <span className="text-blue-600 font-extrabold">SMARTER</span> TOMORROW
                </p>
              </div>

              {/* Cyan Accent Bar Underline */}
              <div className="w-10 h-0.75 bg-blue-500 rounded-full mt-2.5 mb-1" />

              {/* Welcome Back & Subtitle */}
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight mt-3">
                SuperAdmin Access
              </h1>
              <p className="text-xs sm:text-[13px] text-slate-500 mt-0.5 font-normal">
                Sign in to Platform Control Tower
              </p>
            </div>

            {/* Root Enclave Badge */}
            <div className="mt-3 p-2 bg-indigo-50/90 border border-indigo-200/80 rounded-xl flex items-center gap-2 shadow-2xs">
              <div className="w-6 h-6 rounded-lg bg-indigo-600 text-white flex items-center justify-center shrink-0 shadow-xs">
                <ShieldAlert className="w-3.5 h-3.5" />
              </div>
              <div className="min-w-0 flex-1 text-left">
                <span className="block text-xs font-bold text-slate-900 truncate">Root Control Enclave</span>
                <span className="block text-[9.5px] text-slate-500 truncate">Cryptographically Verified Hardware Session</span>
              </div>
            </div>

            {/* B. 2FA SCREEN OR LOGIN FORM */}
            {twoFactorData ? (
              /* 2FA VERIFICATION STEP */
              <div className="mt-5 space-y-4 animate-in fade-in slide-in-from-right-3 duration-200">
                <div className="text-center space-y-1">
                  <div className="w-10 h-10 mx-auto rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 mb-2">
                    <Smartphone className="w-5 h-5" />
                  </div>
                  <h2 className="text-base font-bold text-slate-900">
                    Security Verification
                  </h2>
                  <p className="text-xs text-slate-500">
                    Enter the 6-digit code from your authenticator app
                  </p>
                  <p className="text-xs font-semibold text-slate-700 font-mono">
                    {twoFactorData.email}
                  </p>
                </div>

                <form onSubmit={handleVerify2FA} className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-bold text-slate-700">
                        Security Code
                      </label>
                      <button
                        type="button"
                        onClick={() => setTwoFactorCode('123456')}
                        className="text-[10.5px] font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1 cursor-pointer bg-blue-50 px-2 py-0.5 rounded border border-blue-200"
                      >
                        <span>⚡ Quick Code (123456)</span>
                      </button>
                    </div>
                    <div className="relative">
                      <KeyRound className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        type="text"
                        required
                        autoFocus
                        maxLength={12}
                        value={twoFactorCode}
                        onChange={(e) => setTwoFactorCode(e.target.value)}
                        className="w-full pl-10 pr-3 py-2.5 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 rounded-xl text-base font-mono font-bold tracking-widest text-slate-900 focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-600 transition-all text-center"
                        placeholder="123456"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isVerifying2FA}
                    className="w-full py-2.5 bg-[#1a73e8] hover:bg-blue-700 text-white font-semibold text-xs sm:text-sm rounded-xl shadow-md shadow-blue-500/20 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
                  >
                    {isVerifying2FA ? (
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    <span>{isVerifying2FA ? 'Verifying...' : 'Verify & Enter Enclave'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => { setTwoFactorData(null); setTwoFactorCode(''); }}
                    className="w-full py-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    <span>Back to login</span>
                  </button>
                </form>
              </div>
            ) : (
              /* STANDARD SUPERADMIN LOGIN FORM */
              <div className="mt-5 space-y-3.5">
                
                {/* Notice: No Client Workspace or Vendor Portal buttons/links */}

                <form onSubmit={handleSubmit} className="space-y-3">
                  
                  {/* 1. Email or Username Input */}
                  <div>
                    <div className="relative rounded-xl border border-slate-200 bg-white hover:border-slate-300 focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-500/10 transition-all shadow-2xs">
                      <User className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        type="text"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-10 pr-3.5 py-2.5 sm:py-3 text-xs sm:text-[13px] font-medium text-slate-900 placeholder-slate-400 bg-transparent rounded-xl focus:outline-none"
                        placeholder="SuperAdmin Master Email"
                      />
                    </div>
                  </div>

                  {/* 2. Password Input with Toggle */}
                  <div>
                    <div className="relative rounded-xl border border-slate-200 bg-white hover:border-slate-300 focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-500/10 transition-all shadow-2xs">
                      <Lock className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full pl-10 pr-10 py-2.5 sm:py-3 text-xs sm:text-[13px] font-medium text-slate-900 placeholder-slate-400 bg-transparent rounded-xl focus:outline-none"
                        placeholder="Master Security Password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 cursor-pointer transition-colors"
                        title={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* 3. Controls Row: Remember me + Forgot password? */}
                  <div className="flex items-center justify-between pt-0.5">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded cursor-pointer"
                      />
                      <span className="text-xs text-slate-600 font-medium">Remember device</span>
                    </label>

                    <button 
                      type="button"
                      onClick={() => { setRecoveryEmail(email); setForgotModalOpen(true); }}
                      className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
                    >
                      Emergency Recovery?
                    </button>
                  </div>

                  {/* 4. Primary Sign In Button */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-2.5 sm:py-3 px-4 bg-[#1a73e8] hover:bg-blue-600 text-white font-semibold text-xs sm:text-[13.5px] rounded-xl shadow-md shadow-blue-500/25 hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60 mt-1"
                  >
                    {isLoading ? (
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : null}
                    <span>{isLoading ? 'Authenticating Enclave...' : 'Sign In as SuperAdmin'}</span>
                  </button>
                </form>

                {/* 5. SSO Divider */}
                <div className="relative my-3 flex items-center justify-center">
                  <div className="w-full border-t border-slate-200" />
                  <span className="bg-white px-3 text-[11px] sm:text-xs text-slate-400 shrink-0 font-medium select-none">
                    or continue with
                  </span>
                </div>

                {/* 6. SSO Buttons Row (Microsoft, Google, SSO) */}
                <div className="grid grid-cols-3 gap-2">
                  
                  {/* Microsoft Button */}
                  <button
                    type="button"
                    onClick={() => handleSSO('Microsoft')}
                    className="flex items-center justify-center gap-1.5 py-2 px-2 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-xl text-xs font-semibold text-slate-700 shadow-2xs transition-all cursor-pointer group"
                  >
                    <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 21 21">
                      <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                      <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                      <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                    </svg>
                    <span className="truncate">Microsoft</span>
                  </button>

                  {/* Google Button */}
                  <button
                    type="button"
                    onClick={() => handleSSO('Google')}
                    className="flex items-center justify-center gap-1.5 py-2 px-2 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-xl text-xs font-semibold text-slate-700 shadow-2xs transition-all cursor-pointer group"
                  >
                    <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z"/>
                      <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z"/>
                      <path fill="#FBBC05" d="M5.28 14.27A7.18 7.18 0 0 1 4.9 12c0-.79.14-1.57.38-2.27V6.58H1.25A11.97 11.97 0 0 0 0 12c0 1.92.45 3.74 1.25 5.42l4.03-3.15z"/>
                      <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z"/>
                    </svg>
                    <span className="truncate">Google</span>
                  </button>

                  {/* SSO Button */}
                  <button
                    type="button"
                    onClick={() => handleSSO('Enterprise SSO')}
                    className="flex items-center justify-center gap-1.5 py-2 px-2 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-xl text-xs font-semibold text-slate-700 shadow-2xs transition-all cursor-pointer group"
                  >
                    <Key className="w-3.5 h-3.5 text-slate-700 group-hover:text-blue-600 transition-colors" />
                    <span className="truncate">SSO</span>
                  </button>

                </div>

                {/* 7. Security & Enclave Information */}
                <div className="pt-2 text-center space-y-2">
                  <p className="text-[11px] sm:text-xs text-slate-500 font-normal">
                    Authorized Sanglob Platform personnel only.
                  </p>
                  <div className="pt-1.5 border-t border-slate-100">
                    <Link 
                      to="/login" 
                      className="text-[11px] sm:text-xs text-indigo-600 hover:text-indigo-800 font-semibold transition-colors inline-flex items-center gap-1"
                    >
                      Standard User or Client? Sign in to Client Workspace →
                    </Link>
                  </div>
                </div>

              </div>
            )}

          </div>
        </div>

      </main>

      {/* 3. FIXED BOTTOM GLOBAL FOOTER BAR */}
      <footer className="fixed bottom-0 left-0 right-0 w-full bg-white/95 backdrop-blur-md border-t border-slate-200/90 px-6 sm:px-10 py-2 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500 z-30 shadow-xs">
        <div className="flex items-center gap-2 text-slate-600 flex-wrap">
          <span className="font-semibold text-slate-800">SOC 2 & ISO Certified</span>
          <span>•</span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block animate-pulse" />
            <span className="text-emerald-700 font-medium">99.99% Availability</span>
          </span>
          <span>•</span>
          <span>Secure Root Enclave</span>
          <span>•</span>
          <span className="font-medium text-slate-700">Sanglob Cloud</span>
        </div>
        <div className="text-slate-500 text-[11px]">
          <span>Protected by Cryptographic RSA-4096 & Zero-Trust RBAC · © ${new Date().getFullYear()} Sanglob Platform</span>
        </div>
      </footer>

      {/* EMERGENCY RECOVERY MODAL */}
      {forgotModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-200 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-blue-700">
                <ShieldAlert className="w-5 h-5 text-blue-600" />
                <h3 className="font-bold text-slate-900 text-base">SuperAdmin Access Recovery</h3>
              </div>
              <button 
                onClick={() => setForgotModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <p className="text-xs text-slate-600 leading-relaxed">
              Dispatches a cryptographic password reset token to your verified administrative email address.
            </p>

            <form onSubmit={handleRecoverySubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Administrative Email</label>
                <div className="relative rounded-xl border border-slate-200 bg-white">
                  <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="email"
                    required
                    value={recoveryEmail}
                    onChange={(e) => setRecoveryEmail(e.target.value)}
                    placeholder="connect@sanglob.com"
                    className="w-full pl-9 pr-3 py-2 text-xs text-slate-900 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setForgotModalOpen(false)}
                  className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingRecovery}
                  className="px-4 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors cursor-pointer disabled:opacity-60"
                >
                  {isSubmittingRecovery ? 'Sending...' : 'Dispatch Token'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MICROSOFT 365 ENTRA ID SSO MODAL */}
      {msModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-200 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <svg className="w-5 h-5 shrink-0" viewBox="0 0 21 21">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                </svg>
                <div>
                  <h3 className="font-bold text-slate-900 text-sm sm:text-base">Microsoft Entra ID Authentication</h3>
                  <p className="text-[11px] text-slate-500">Sign in with authorized Microsoft 365 Work or School account</p>
                </div>
              </div>
              <button 
                onClick={() => setMsModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-3 bg-blue-50/80 border border-blue-200/80 rounded-xl space-y-1">
              <div className="flex items-center gap-1.5 text-xs font-bold text-blue-950">
                <ShieldCheck className="w-4 h-4 text-blue-600" />
                <span>Zero-Trust Enterprise Federation</span>
              </div>
              <p className="text-[11px] text-blue-900 leading-relaxed">
                Connects through Microsoft identity federation with cryptographic root access validation for Platform SuperAdmins.
              </p>
            </div>

            <form onSubmit={handleConfirmMicrosoftSSO} className="space-y-3 pt-1">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Microsoft 365 SuperAdmin Account
                </label>
                <div className="relative rounded-xl border border-slate-200 bg-white">
                  <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="email"
                    required
                    value={msEmail}
                    onChange={(e) => setMsEmail(e.target.value)}
                    placeholder="connect@sanglob.com"
                    className="w-full pl-9 pr-3 py-2.5 text-xs font-medium text-slate-900 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setMsModalOpen(false)}
                  className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={msLoading}
                  className="px-4 py-2 text-xs font-semibold text-white bg-[#0078d4] hover:bg-[#106ebe] rounded-xl shadow-md transition-all cursor-pointer disabled:opacity-60 flex items-center gap-2"
                >
                  {msLoading ? (
                    <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 21 21">
                      <rect x="1" y="1" width="9" height="9" fill="#ffffff"/>
                      <rect x="11" y="1" width="9" height="9" fill="#ffffff"/>
                      <rect x="1" y="11" width="9" height="9" fill="#ffffff"/>
                      <rect x="11" y="11" width="9" height="9" fill="#ffffff"/>
                    </svg>
                  )}
                  <span>{msLoading ? 'Verifying Identity...' : 'Sign in with Microsoft'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
